import {
  TableRoot,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
} from "components/Graphs/Table";
import React, { useMemo, useState } from "react";
import { AdminUserType } from "../../types";
import Pagination from "components/Pagination";
import { useGetUsers } from "components/Admin/hooks/useAdminUsers";
import { Button, CopyButton, FlatCard } from "components/UI-lib";
import { UsersIcon } from "@heroicons/react/24/outline";
import { Input } from "components/Graphs/Input";

export const TeamList: React.FC<{ orgId: string }> = ({ orgId }) => {
  const { users } = useGetUsers({ orgId });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  // Filter and sort users based on search term
  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users;

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = users.filter((user) => {
        const name = user.Name?.toLowerCase() || "";
        const email = user.Email?.toLowerCase() || "";
        const id = user.id?.toLowerCase() || "";

        return (
          name.includes(searchLower) ||
          email.includes(searchLower) ||
          id.includes(searchLower)
        );
      });
    }

    // Sort by name
    return filtered.sort((a, b) => {
      const nameA = a.Name || a.id || "";
      const nameB = b.Name || b.id || "";
      return nameA.localeCompare(nameB);
    });
  }, [users, searchTerm]);

  // Pagination
  const totalUsersCount = filteredAndSortedUsers.length;
  const itemsPerPage = 8;
  const totalPages = Math.ceil(totalUsersCount / itemsPerPage);

  // Calculate items to display on current page
  const startIndex = (currentPage - 1) * itemsPerPage;
  const usersToDisplay = useMemo(() => {
    return filteredAndSortedUsers.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedUsers, startIndex, itemsPerPage]);

  return (
    <FlatCard>
      <div className="flex flex-row justify-between">
        <div>
          <div className="flex flex-row gap-2 items-center">
            <UsersIcon className="h-5 w-5 text-primary" />
            <h1 className="text-xl font-bold text-primary">Team</h1>
          </div>
          <p className="text-secondary text-sm">
            View members of this organization.
          </p>
        </div>
        <p className="text-secondary text-lg">
          Total Members: {totalUsersCount}
        </p>
      </div>
      <hr className="w-[100%] mb-3 mt-3 dark:border dark:border-zinc-800" />
      {users.length > 0 && (
        <div className="mb-4">
          <Input
            type="search"
            placeholder="Search by name, email, or ID..."
            value={searchTerm}
            onChange={(e) => {
              if (currentPage !== 1) {
                setCurrentPage(1);
              }
              setSearchTerm(e.target.value);
            }}
            className="max-w-full"
          />
        </div>
      )}
      <TableRoot>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell>Email</TableHeaderCell>
              <TableHeaderCell>ID</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {usersToDisplay.length > 0 ? (
              usersToDisplay.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <span className="text-sm text-gray-500 dark:text-secondary ">
                      <CopyButton value={item.Name} />
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-500 dark:text-secondary">
                      <CopyButton value={item.Email} />
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-500 dark:text-secondary ">
                      <CopyButton value={item.id} />
                    </span>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2}>
                  <div className="text-center py-4">
                    <span className="text-sm text-gray-700 dark:text-secondary">
                      {searchTerm.trim()
                        ? `No users found matching "${searchTerm}"`
                        : "No users found"}
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableRoot>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </FlatCard>
  );
};
